"use client";

import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";
import { studentImportApi, type StudentColumnMapping } from "@/lib/student-import-api.service";
import { Toast, getSafeErrorMessage } from "@/lib/utils";

/**
 * Read-only reference table: DB column ↔ xlsx header ↔ explanation, sourced
 * from the backend's `student-xlsx-columns.ts` (via `/v1/student-import/columns`)
 * — never a hand-copied table, so it cannot drift from the actual import
 * parser the moment a column changes.
 */
export default function StudentColumnsReference() {
	const [columns, setColumns] = useState<StudentColumnMapping[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState("");

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		studentImportApi
			.listColumns()
			.then((data) => {
				if (!cancelled) setColumns(data);
			})
			.catch((err) => {
				if (cancelled) return;
				const message = getSafeErrorMessage(err);
				setError(message);
				Toast.error("Không tải được danh sách cột", message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const filtered = useMemo(() => {
		if (!columns) return [];
		const q = search.trim().toLowerCase();
		if (!q) return columns;
		return columns.filter(
			(c) =>
				c.dbColumn.toLowerCase().includes(q) ||
				(c.xlsxHeader ?? "").toLowerCase().includes(q) ||
				c.description.toLowerCase().includes(q),
		);
	}, [columns, search]);

	if (loading) {
		return (
			<Card>
				<CardContent className="p-4 space-y-3">
					<Skeleton className="h-9 w-64" />
					<div className="space-y-2">
						{Array.from({ length: 6 }).map((_, i) => (
							<Skeleton key={i} className="h-8 w-full" />
						))}
					</div>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		return (
			<Card>
				<CardContent className="p-4 text-sm text-red-600 dark:text-red-400">{error}</CardContent>
			</Card>
		);
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-3">
				<div className="relative flex-1 max-w-sm">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Tìm theo tên cột DB hoặc XLSX..."
						className="pl-9"
					/>
				</div>
				<span className="text-sm text-muted-foreground">
					{filtered.length}/{columns?.length ?? 0} cột
				</span>
			</div>

			<Card>
				<CardContent className="p-0">
					<div className="max-h-[28rem] overflow-y-auto">
						<table className="w-full text-sm">
							<thead className="sticky top-0 z-10 bg-muted">
								<tr>
									<th className="px-4 py-2 text-left font-medium text-muted-foreground">
										Tên cột DB
									</th>
									<th className="px-4 py-2 text-left font-medium text-muted-foreground">
										Tên cột trong file XLSX
									</th>
									<th className="px-4 py-2 text-left font-medium text-muted-foreground">
										Giải thích
									</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map((c) => (
									<tr key={c.dbColumn} className="border-t">
										<td className="px-4 py-2 font-mono text-xs text-foreground">{c.dbColumn}</td>
										<td className="px-4 py-2 font-mono text-xs text-muted-foreground">
											{c.xlsxHeader ?? "—"}
										</td>
										<td className="px-4 py-2 text-muted-foreground">{c.description}</td>
									</tr>
								))}
								{filtered.length === 0 && (
									<tr>
										<td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">
											Không tìm thấy cột phù hợp
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
